import React, {useRef, useState, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import RunsService from '../../service/runs'
import {PageTitle} from '../../_metronic/layout/core'

const loadIgv = async () => {
  const igvModule = await import('igv/dist/igv.esm1')
  console.log(igvModule)
  return igvModule.default || igvModule
}

const IGVPage = () => {
  const {igvParams} = useParams()
  const container = useRef(null)
  const runId = igvParams.split('&')[0]
  const locus = igvParams.split('&')[1] || 'chr9'
  const vType = igvParams.split('&')[2] || 'snv'
  const sType = igvParams.split('&')[3] || 'somatic'

  const [browser, setBrowser] = useState(null)
  const [igv, setIgv] = useState(null)
  const [loading, setLoading] = useState(true)
  const [runDetails, setRunDetails] = useState(null)
  const [tracks, setTracks] = useState([])
  const [windowHeight, setWindowHeight] = useState(window.innerHeight - 460)
  const [loadingTracks, setLoadingTracks] = useState({})  // Track loading states by ID

  useEffect(() => {
    const fetchIgv = async () => {
      const igvInstance = await loadIgv()
      setIgv(igvInstance)
    }
    fetchIgv()
  }, [])

  useEffect(() => {
    if (igv && !browser) {
      createBrowser()
    }
  }, [igv])

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight - 460)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const createBrowser = async () => {
    if (!igv || browser) return

    setLoading(true)
    const runDetailsResponse = await RunsService.getRunDetail(runId)
    const runData = runDetailsResponse.data
    setRunDetails(runData)

    const sampleName = runData.samples[0]?.sampleId
    const baseUrl = `https://${runData.onNode}.node.gennext.bio/api/v0/files/`
    const jwtToken = JSON.parse(localStorage.getItem('user'))?.token

    const trackList = [
      {
        id: 'bam',
        type: 'alignment',
        format: 'bam',
        name: `${sampleName} BAM`,
        url: `${baseUrl}alignment/${runId}?type=bam&sampleName=${sampleName}`,
        indexURL: `${baseUrl}alignment/${runId}/index?type=bam&sampleName=${sampleName}`,
        height: 300,
        enabled: true,
        status: 'pending',
        displayName: `${sampleName} BAM`,
        oauthToken: jwtToken,
      },
      {
        id: 'vcf',
        type: 'variant',
        format: 'vcf',
        name: `${sampleName} ${vType.toUpperCase()} VCF`,
        url: `${baseUrl}alignment/${runId}?sType=${sType}&vType=${vType}&type=vcf&sampleName=${sampleName}`,
        indexURL: `${baseUrl}alignment/${runId}/index?sType=${sType}&vType=${vType}&type=vcf&sampleName=${sampleName}`,
        showGenotypes: false,
        enabled: true,
        status: 'pending',
        displayName: `${sampleName} ${vType.toUpperCase()} VCF`,
        oauthToken: jwtToken,
      },
      {
        id: 'cnvpytor',
        type: 'cnvpytor',
        name: `${sampleName} BAF likelihood`,
        indexed: true,
        url: `${baseUrl}alignment/${runId}?sType=${sType}&vType=${vType}&type=cnvpytor&sampleName=${sampleName}`,
        signal_name: 'snp',
        bin_size: 100000,
        height: 180,
        enabled: false,
        status: 'pending',
        displayName: 'CNVpytor Data',
        oauthToken: jwtToken,
      },
      {
        id: 'read_depth',
        type: 'wig',
        format: 'bedgraph',
        name: `${sampleName} Kit Regions & Read Depth`,
        url: `${baseUrl}alignment/${runId}?sType=${sType}&vType=${vType}&type=bed&sampleName=${sampleName}`,
        indexURL: `${baseUrl}alignment/${runId}/index?sType=${sType}&vType=${vType}&type=bed&sampleName=${sampleName}`,
        enabled: true,
        status: 'pending',
        displayName: `${sampleName} Read Depth`,
        oauthToken: jwtToken,
      },
    ]

    // Initialize loadingTracks state
    const initialLoadingState = {}
    trackList.forEach(track => {
      initialLoadingState[track.id] = track.enabled
    })
    setLoadingTracks(initialLoadingState)
    
    setTracks(trackList)

    const igvOptions = {
      reference: {
        id: 'hg38',
        name: 'Human (GRCh38/hg38)',
        fastaURL: 'https://igv.org/genomes/data/hg38/hg38.fa',
        indexURL: 'https://igv.org/genomes/data/hg38/hg38.fa.fai',
      },
      locus: locus,
      oauthToken: jwtToken,
      tracks: trackList.filter((t) => t.enabled),
    }
    
    const newBrowser = await igv.createBrowser(container.current, igvOptions)
    
    // Add track load event listeners for initially loaded tracks
    trackList.filter(t => t.enabled).forEach(track => {
      listenForTrackEvents(newBrowser, track.id)
    })
    
    setBrowser(newBrowser)
    setLoading(false)
  }
  
  // Listen for track load/error events
  const listenForTrackEvents = (browserInstance, trackId) => {
    if (!browserInstance) return
    
    // Find the track view for this track
    const trackView = browserInstance.trackViews.find(tv => 
      tv.track.id === trackId || tv.track.name === tracks.find(t => t.id === trackId)?.name
    )
    
    if (trackView) {
      // The track is loaded successfully
      setLoadingTracks(prev => ({...prev, [trackId]: false}))
      setTracks(prev => prev.map(t => t.id === trackId ? {...t, status: 'loaded'} : t))
    } else {
      // Set a timeout to check again in case track is still being added
      setTimeout(() => {
        const trackView = browserInstance.trackViews.find(tv => 
          tv.track.id === trackId || tv.track.name === tracks.find(t => t.id === trackId)?.name
        )
        if (trackView) {
          setLoadingTracks(prev => ({...prev, [trackId]: false}))
          setTracks(prev => prev.map(t => t.id === trackId ? {...t, status: 'loaded'} : t))
        }
      }, 2000)
    }
  }

  const toggleTrack = async (trackId, enable) => {
    if (!browser) return
    
    // Find the track configuration
    const track = tracks.find((t) => t.id === trackId)
    if (!track) return
    
    // Set loading state for this track
    setLoadingTracks(prev => ({...prev, [trackId]: true}))
    
    if (enable) {
      // Check if track is already loaded to prevent duplicates
      const isTrackAlreadyLoaded = browser.trackViews.some(tv => 
        tv.track.id === trackId || tv.track.name === track.name
      )
      
      if (!isTrackAlreadyLoaded) {
        const newTrack = {...track}
        delete newTrack.enabled
        
        try {
          // Add track load event listener
          await browser.loadTrack(newTrack)
          listenForTrackEvents(browser, trackId)
        } catch (error) {
          console.error(`Error loading track ${trackId}:`, error)
          setLoadingTracks(prev => ({...prev, [trackId]: false}))
          setTracks(prev => prev.map(t => t.id === trackId ? {...t, enabled: false, status: 'error'} : t))
          return
        }
      } else {
        // Track already exists, don't add it again
        console.log(`Track ${trackId} is already loaded`)
        setLoadingTracks(prev => ({...prev, [trackId]: false}))
        return
      }
    } else {
      // Remove track
      const existingTrack = browser.trackViews.find((tv) => tv.track.name === track.name)?.track
      if (existingTrack) {
        await browser.removeTrack(existingTrack)
      }
      setLoadingTracks(prev => ({...prev, [trackId]: false}))
    }
    
    // Update track state
    setTracks((prev) => prev.map((t) => (t.id === trackId ? {...t, enabled: enable} : t)))
  }

  const renderTrackControls = () => (
    <div className='p-5 bg-light rounded mt-4'>
      <div className='d-flex gap-3 flex-column justify-content-between mb-3'>
        <h5 className='mb-0'>Track Controls</h5>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <button
            className='btn btn-sm btn-success me-2'
            onClick={() => tracks.forEach((t) => !t.enabled && toggleTrack(t.id, true))}
            disabled={Object.values(loadingTracks).some(loading => loading)}
          >
            Enable All
          </button>
          <button
            className='btn btn-sm btn-secondary'
            onClick={() => tracks.forEach((t) => t.enabled && toggleTrack(t.id, false))}
            disabled={Object.values(loadingTracks).some(loading => loading)}
          >
            Disable All
          </button>
        </div>
      </div>
      {tracks.map(
        (track) =>
          track.id && (
            <div
              key={track.id}
              className='d-flex justify-content-between align-items-center mb-2 p-2 bg-white border rounded'
            >
              <span>{track.name}</span>
              <div className='d-flex align-items-center gap-2'>
                <div className={`status-indicator ${track.enabled ? 'status-on' : 'status-off'}`}></div>
                {loadingTracks[track.id] && (
                  <div className='spinner-grow spinner-grow-sm text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                )}
                <label className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={track.enabled}
                    onChange={(e) => toggleTrack(track.id, e.target.checked)}
                    disabled={loadingTracks[track.id]}
                  />
                </label>
              </div>
            </div>
          )
      )}
    </div>
  )

  return (
    <div className='card'>
      <PageTitle breadcrumbs={[]}>
        {runDetails?.id ? `${runDetails.samples[0].sampleId} - IGV` : 'IGV'}
      </PageTitle>
      {loading && (
        <div className='loading-spinner'>
          <p>Loading IGV...</p>
        </div>
      )}
      <div className='row mx-10 mt-5'>
        <div className='col-lg-9'>
          <div ref={container}></div>
        </div>
        <div className='col-lg-3'>{renderTrackControls()}</div>
      </div>
    </div>
  )
}

export default IGVPage